import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  cloneElement,
} from 'react';

import { useFilterBarContext } from './hooks/useFilterBarContext.js';
import { FilterBarActions } from './types/filter-state.types.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { FormField } from '../../forms/forms-core/components/form-field/FormField.js';
import { Label } from '../../forms/forms-core/components/label/Label.js';
import type {
  FormControlRef,
  FormControlWithOverlayRef,
} from '../../forms/shared-types.js';
import { isFormControlWithOverlayRef } from '../../forms/shared-types.js';

//#region Typings
/**
 * @public
 */
export interface FilterBarItemProps
  extends StylingProps,
    DataTestId,
    BehaviorTrackingProps {
  /** A unique identifier for this sub-filter (must only be unique for this filter). In case custom labels are used without text children, this is displayed on the MoreMenu trigger instead. */
  name: string;
  /** Description text of this sub-filter. If you use a custom label without text children, the name will be displayed on the MoreMenu instead. */
  label: ReactNode;
  /** Only one element, is expected here. */
  // TODO: React 19 update - We will need to check if limiting the props here actually has an effect to the public api validation.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: ReactElement<any>;
  /** Defines if the label is shown for the filter item. If set specifically, it also overwrites the general configuration set with the showLabels prop on the FilterBar for one item. */
  showLabel?: boolean;
}
//#endregion Typings

const InternalItem = (props: FilterBarItemProps) => {
  const {
    name,
    label,
    children: child,
    showLabel: propShowLabel,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: FilterBarItemProps = props;
  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);
  const { defaultValue, value, onChange, ...propsWithoutContractProperties } =
    child.props;
  const [filterValue, setFilterValue] = useControllableState({
    defaultValue,
    value,
    onChange,
  });

  const { dispatch, showLabels, lastAddedItem, state } = useFilterBarContext();
  const showLabel = propShowLabel ?? showLabels;
  const isStringLabel = typeof label === 'string';

  const filterItemRef = useRef<FormControlRef | FormControlWithOverlayRef>(
    null,
  );

  if (!isStringLabel && !showLabel && isDev()) {
    // eslint-disable-next-line no-console
    console.warn(
      "FilterBar.Item: the showLabel prop was set to false but the provided label can't be used as aria-label because it is not a string. " +
        'Please provide a string-based label or set showLabel to true for this FilterBar.Item.',
    );
  }

  // Is only called on the first render or if the name changes when conditionally rendering it, for the context to have the proper initial state.
  useEffect(() => {
    dispatch({
      type: FilterBarActions.INITIALIZE,
      payload: {
        name,
        value: filterValue,
      },
    });

    setTimeout(() => {
      // If the item was added via the addFilter-dropdown the newly added item should be focused or opened depending on the control.
      if (lastAddedItem === name) {
        filterItemRef?.current?.focus?.();

        if (
          filterItemRef?.current &&
          isFormControlWithOverlayRef(filterItemRef?.current)
        ) {
          filterItemRef?.current?.open();
        }
      }
    });

    return () => {
      dispatch({
        type: FilterBarActions.REMOVE,
        payload: {
          name,
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  // must trigger the change after rendering to avoid a state update during rendering.
  useEffect(() => {
    if (state.filters?.[name] && filterValue !== state.filters?.[name].value) {
      dispatch({
        type: FilterBarActions.CHANGE,
        payload: {
          name,
          value: filterValue,
        },
      });
    }
    // name is excluded since the Filter.Item must be reinitialized when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, state.filters?.[name]]);

  return (
    <FormField
      data-testid={dataTestId}
      id={propsWithoutContractProperties.id}
      className={consumerClassName}
      style={consumerStyle}
      {...behavioralTrackingProps}
    >
      {showLabel && <Label>{label}</Label>}
      {cloneElement(child, {
        ..._mergeProps(propsWithoutContractProperties, {
          value: filterValue,
          onChange: setFilterValue,
          'aria-label': isStringLabel && !showLabel ? label : undefined,
          ref: filterItemRef,
        }),
      })}
    </FormField>
  );
};

/**
 * FilterBar.Item component, used for rendering custom filters inside the filter bar. Requires a unique name prop in order to keep track of the filter.
 * @public
 */
export const Item = (props: FilterBarItemProps) => {
  /* We need to pass a key so that we invalidate the useControllableState hook usage and remount the component when rendered conditionally.
   This is a code sample of the issue and possible fixes: https://codesandbox.io/s/smoosh-frog-q57xiw?file=/src/App.tsx
   Also, this is a blog post describing the key issue in more detail: https://www.nikgraf.com/blog/using-reacts-key-attribute-to-remount-a-component */
  return <InternalItem {...props} key={props.name} />;
};
Item['displayName'] = 'FilterBar.Item';
